import React from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';

const services = [
  {
    icon: 'fa-solid fa-tv',
    title: '1. OTT & Video Streaming Platforms',
    desc: 'Build high-performance, cross-platform video streaming applications (AVOD, SVOD, TVOD) featuring adaptive bitrate streaming (HLS/DASH) and multi-device support.',
  },
  {
    icon: 'fa-solid fa-wand-magic-sparkles',
    title: '2. AI Video Processing & Automated Metadata',
    desc: 'Leverage computer vision and NLP to automate video indexing, facial recognition, scene detection, closed caption generation, and content tagging.',
  },
  {
    icon: 'fa-solid fa-shield-cat',
    title: '3. Multi-DRM & Content Protection Systems',
    desc: 'Safeguard premium video assets with enterprise Multi-DRM (Widevine, FairPlay, PlayReady), forensic watermarking, and anti-piracy leak monitoring.',
  },
  {
    icon: 'fa-solid fa-tower-cell',
    title: '4. Live Cloud Broadcasting & Low-Latency Encoding',
    desc: 'Architect low-latency (LL-HLS / WebRTC) Cloud Live workflows with automated failover, dynamic cloud transcoding, and multi-region CDN distribution.',
  },
  {
    icon: 'fa-solid fa-rectangle-ad',
    title: '5. Dynamic Ad Insertion (DAI) & FAST Channels',
    desc: 'Monetize live and VOD content with Server-Side Ad Insertion (SSAI), targeted ad decision engines, and turn-key Free Ad-Supported Streaming TV (FAST) channels.',
  },
  {
    icon: 'fa-solid fa-box-archive',
    title: '6. Digital Asset Management (DAM) & Archiving',
    desc: 'Centralize, index, and manage petabyte-scale media archives with cloud-native DAM platforms, automated proxy generation, and deep tape-to-cloud workflows.',
  },
  {
    icon: 'fa-solid fa-headphones',
    title: '7. Immersive Audio & Spatial Media Processing',
    desc: 'Deliver cinema-grade experiences with Dolby Atmos, spatial audio encoding, multi-language audio track switching, and real-time audio ducking pipelines.',
  },
  {
    icon: 'fa-solid fa-users-viewfinder',
    title: '8. Audience Engagement & Personalization Engines',
    desc: 'Increase viewer retention using real-time ML recommendation algorithms, interactive polling, live watch parties, and custom user behavior dashboards.',
  },
  {
    icon: 'fa-solid fa-vr-cardboard',
    title: '9. Virtual Production & AR/VR Experiences',
    desc: 'Power next-generation entertainment through Unreal Engine virtual set integration, spatial 3D video pipelines, and interactive augmented reality apps.',
  },
  {
    icon: 'fa-solid fa-chart-line',
    title: '10. Quality of Experience (QoE) & Stream Analytics',
    desc: 'Track startup time, rebuffering ratios, bitrate fluctuations, and audience engagement metrics in real time using client-side telemetry SDKs.',
  },
];

const whyChooseUs = [
  'End-to-end expertise in high-concurrency OTT and live event streaming',
  'Multi-DRM protection compliant with Hollywood studio standards',
  'Ultra-low-latency video delivery using WebRTC and LL-HLS protocols',
  'Seamless integration with major CDN networks (Cloudfront, Akamai, Fastly)',
  'Turn-key FAST channel creation with automated SSAI monetization',
  'AI-driven media asset tagging and automated captioning engines',
  'Cross-platform smart TV apps (Tizen, webOS, Apple TV, Fire TV, Roku)',
  'Proven multi-region cloud encoding and high-density transcoding pipelines',
  'Client-side and server-side QoE analytics monitoring 24/7',
  'Deep domain expertise across sports broadcasting, film, and digital publishers',
];

const processSteps = [
  { step: '01', title: 'Media Architecture Assessment', desc: 'Audit current playback pipelines, encoding profiles, CDN infrastructure, and content security frameworks.' },
  { step: '02', title: 'Platform & Workflow Blueprinting', desc: 'Design scalable cloud ingestion, Multi-DRM key management, and multi-device app architectures.' },
  { step: '03', title: 'Encoding & App Development', desc: 'Construct adaptive streaming pipelines, build native multi-platform UIs, and integrate ad decision servers.' },
  { step: '04', title: 'Security & Load Stress Testing', desc: 'Execute DRM verification, load-test live concurrency spikes, and optimize CDN caching strategies.' },
  { step: '05', title: 'Live Broadcast & App Rollout', desc: 'Launch platforms across web, mobile, and Smart TV app stores with continuous playback monitoring.' },
  { step: '06', title: 'QoE Optimization & AI Analytics', desc: 'Continuously analyze QoE metrics, reduce rebuffer rates, and refine recommendation algorithms.' },
];

const techStack = [
  { category: 'Streaming & Video Core', items: ['HLS / LL-HLS', 'MPEG-DASH', 'WebRTC', 'FFmpeg', 'AWS Elemental'] },
  { category: 'Content Security & Adtech', items: ['Widevine', 'Apple FairPlay', 'Microsoft PlayReady', 'VAST / VPAID', 'Google IMA'] },
  { category: 'Player & Cross-Platform', items: ['Video.js', 'Shaka Player', 'ExoPlayer', 'React Native', 'Flutter'] },
  { category: 'Cloud & Analytics', items: ['AWS MediaServices', 'Bitmovin', 'Mux', 'Conviva', 'Akamai'] },
];

// Animation Variants
const containerVariants = {
  hidden: { opacity: 0 },
  visible: {
    opacity: 1,
    transition: { staggerChildren: 0.12 },
  },
};

const cardVariants = {
  hidden: { opacity: 0, y: 30 },
  visible: { opacity: 1, y: 0, transition: { duration: 0.5, ease: 'easeOut' } },
};

const MediaAndEntertainment = () => {
  return (
    <div className="service-page text-white overflow-hidden">
      {/* 1. HERO SECTION */}
      <section className="hero-sub-section text-center d-flex align-items-center justify-content-center">
        <div className="container py-5">
          <motion.div
            initial={{ opacity: 0, y: -20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
          >
            <span className="badge rounded-pill bg-outline-cyan px-3 py-2 mb-3 glow-badge">
              Lidlbritain Media Innovations
            </span>
            <h1 className="display-4 fw-bold mb-3">
              Media & Entertainment <span className="gradient-text-cyan">Solutions</span>
            </h1>
            <p className="lead mx-auto opacity-75 max-width-800">
              At <strong>Lidlbritain</strong>, we power the future of digital entertainment. From high-concurrency OTT streaming and AI video enrichment to multi-DRM security and live broadcast workflows, we build engaging media experiences.
            </p>
            <div className="mt-4">
              <motion.div whileHover={{ scale: 1.05 }} whileTap={{ scale: 0.95 }} className="d-inline-block">
                <Link to="/contact" className="btn gradient-btn px-4 py-2 rounded-pill">
                  Scale Your Media Platform &rarr;
                </Link>
              </motion.div>
            </div>
          </motion.div>
        </div>
      </section>

      {/* 2. SERVICES GRID SECTION */}
      <section className="py-5">
        <div className="container">
          <div className="text-center mb-5">
            <h2 className="fw-bold">
              Media & Entertainment Solutions by <span className="gradient-text-orange">Lidlbritain</span>
            </h2>
            <div className="title-line mx-auto mt-2"></div>
          </div>

          <motion.div
            className="row g-4"
            variants={containerVariants}
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true, margin: '-50px' }}
          >
            {services.map((item, index) => (
              <div key={index} className="col-md-6 col-lg-4">
                <motion.div
                  variants={cardVariants}
                  whileHover={{ y: -8, transition: { duration: 0.2 } }}
                  className="service-card p-4 rounded-4 h-100 border border-secondary border-opacity-25 bg-glass"
                >
                  <div className="icon-box mb-3 d-flex align-items-center justify-content-center floating-icon">
                    <i className={`${item.icon} fs-3 text-cyan`}></i>
                  </div>
                  <h5 className="fw-bold mb-3">{item.title}</h5>
                  <p className="text-light opacity-75 small leading-relaxed">{item.desc}</p>
                </motion.div>
              </div>
            ))}
          </motion.div>
        </div>
      </section>

      {/* 3. WHY CHOOSE US & TECH STACK SECTION */}
      <section className="py-5 bg-dark-overlay">
        <div className="container">
          <div className="row g-5 align-items-center">
            {/* Why Choose Lidlbritain */}
            <motion.div
              className="col-lg-6"
              initial={{ opacity: 0, x: -40 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6 }}
            >
              <h3 className="fw-bold mb-4">
                Why Partner With <span className="gradient-text-cyan">Lidlbritain?</span>
              </h3>
              <div className="row g-3">
                {whyChooseUs.map((reason, idx) => (
                  <div key={idx} className="col-sm-6">
                    <div className="d-flex align-items-center gap-2">
                      <i className="fa-solid fa-circle-check text-orange pulse-icon"></i>
                      <span className="small opacity-90">{reason}</span>
                    </div>
                  </div>
                ))}
              </div>
            </motion.div>

            {/* Tools & Tech Stack */}
            <motion.div
              className="col-lg-6"
              initial={{ opacity: 0, x: 40 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6 }}
            >
              <div className="p-4 rounded-4 border border-secondary border-opacity-25 bg-glass glow-box">
                <h4 className="fw-bold mb-4">Media Engineering Ecosystem</h4>
                {techStack.map((tech, idx) => (
                  <div key={idx} className="mb-3">
                    <h6 className="text-cyan mb-2 fw-semibold">{tech.category}</h6>
                    <div className="d-flex flex-wrap gap-2">
                      {tech.items.map((item, i) => (
                        <motion.span
                          whileHover={{ scale: 1.1 }}
                          key={i}
                          className="badge bg-secondary bg-opacity-22 border border-secondary border-opacity-50 text-light fw-normal py-2 px-3"
                        >
                          {item}
                        </motion.span>
                      ))}
                    </div>
                  </div>
                ))}
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* 4. PROCESS SECTION */}
      <section className="py-5">
        <div className="container">
          <div className="text-center mb-5">
            <h2 className="fw-bold">
              The Lidlbritain <span className="gradient-text-orange">Media Delivery Framework</span>
            </h2>
            <div className="title-line mx-auto mt-2"></div>
          </div>

          <motion.div
            className="row g-4"
            variants={containerVariants}
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true }}
          >
            {processSteps.map((stepItem, index) => (
              <div key={index} className="col-md-6 col-lg-4">
                <motion.div
                  variants={cardVariants}
                  whileHover={{ scale: 1.03 }}
                  className="process-card p-4 rounded-4 h-100 bg-glass border border-secondary border-opacity-25 position-relative"
                >
                  <span className="step-number fw-bold text-cyan mb-2 d-block">{stepItem.step}</span>
                  <h5 className="fw-bold mb-2">{stepItem.title}</h5>
                  <p className="text-light opacity-75 small mb-0">{stepItem.desc}</p>
                </motion.div>
              </div>
            ))}
          </motion.div>
        </div>
      </section>

      {/* 5. CALL TO ACTION SECTION */}
      <section className="py-5 my-4">
        <div className="container">
          <motion.div
            initial={{ opacity: 0, scale: 0.9 }}
            whileInView={{ opacity: 1, scale: 1 }}
            viewport={{ once: true }}
            transition={{ duration: 0.5 }}
            className="p-5 rounded-5 cta-banner text-center position-relative overflow-hidden"
          >
            <h2 className="fw-bold mb-3">Reinvent Your Digital Entertainment Platforms</h2>
            <p className="lead opacity-90 mx-auto mb-4 max-width-800">
              Deliver flawless video playback, maximize ad revenue, and secure your digital assets with enterprise-grade cloud media infrastructure.
            </p>
            <motion.div whileHover={{ scale: 1.08 }} whileTap={{ scale: 0.95 }} className="d-inline-block">
              <Link to="/contact" className="btn gradient-btn btn-lg px-5 py-3 rounded-pill fw-semibold">
                Consult with a Media Architect &rarr;
              </Link>
            </motion.div>
          </motion.div>
        </div>
      </section>
    </div>
  );
};

export default MediaAndEntertainment;